import React, { RefObject } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Clock3, Star, UserRound } from 'lucide-react';
import { NavigateFunction } from 'react-router-dom';
import { FreeBadge } from '../FreeBadge';
import type {
  HomeCarouselCategoryTab,
  HomeCarouselCourseCard,
} from '../../utils/homeCourseCarousel';

export type HomeCourseCarouselSectionProps = {
  eyebrow: string;
  titlePrefix: string;
  titleAccent: string;
  titleAccentClassName?: string;
  loopedCards: HomeCarouselCourseCard[];
  categoryTabs: HomeCarouselCategoryTab[];
  activeTab: string;
  onTabChange: (key: string) => void;
  carouselRef: RefObject<HTMLDivElement | null>;
  onScroll: (direction: 'next' | 'prev') => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  navigate: NavigateFunction;
};

const HomeCourseCarouselSection: React.FC<HomeCourseCarouselSectionProps> = ({
  eyebrow,
  titlePrefix,
  titleAccent,
  titleAccentClassName = 'text-[#7c3aed]',
  loopedCards,
  categoryTabs,
  activeTab,
  onTabChange,
  carouselRef,
  onScroll,
  onMouseEnter,
  onMouseLeave,
  navigate,
}) => (
  <section dir="rtl" className="border-t border-slate-200/70 px-2 py-8 sm:px-0 sm:py-16">
    <div className="mb-3 flex items-end justify-between gap-2">
      <div className="text-right">
        <p className="hidden text-sm font-semibold tracking-[0.28em] text-[#1C3B9B] sm:block">{eyebrow}</p>
        <h2 className="text-[1.75rem] font-black leading-tight text-[#0f172a] sm:mt-2 sm:text-4xl">
          <span>{titlePrefix} </span>
          <span className={titleAccentClassName}>{titleAccent}</span>
        </h2>
      </div>
      <div className="mb-3 hidden h-px flex-1 bg-gradient-to-l from-slate-300/20 via-slate-300/55 to-transparent sm:block" />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onScroll('prev')}
          className="hidden h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-[#1C3B9B] shadow-sm transition hover:border-[#1C3B9B] hover:bg-[#1C3B9B]/5 sm:inline-flex"
          aria-label="اسلاید قبلی"
        >
          <ArrowRight className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onScroll('next')}
          className="hidden h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-[#1C3B9B] shadow-sm transition hover:border-[#1C3B9B] hover:bg-[#1C3B9B]/5 sm:inline-flex"
          aria-label="اسلاید بعدی"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => navigate('/courses')}
          className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-[#475569] transition hover:text-[#1C3B9B] sm:gap-2 sm:text-sm"
        >
          <span>همه دوره‌ها</span>
          <ArrowLeft className="h-4 w-4" />
        </button>
      </div>
    </div>

    <div className="mb-4 flex flex-nowrap items-center justify-start gap-2 overflow-x-auto p-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden sm:mb-6 sm:rounded-2xl sm:border sm:border-slate-200 sm:bg-[#eef2ff]/70 sm:p-2">
      {categoryTabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          onClick={() => onTabChange(tab.key)}
          className={`flex-none whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition sm:rounded-xl sm:px-3.5 sm:py-2 sm:text-sm ${
            activeTab === tab.key
              ? 'border-black bg-black text-white sm:border-transparent sm:bg-white sm:text-[#1C3B9B] sm:shadow-sm'
              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-[#1C3B9B] sm:border-transparent sm:bg-transparent sm:hover:bg-white/70'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>

    {loopedCards.length > 0 ? (
      <div className="relative" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        <div
          ref={carouselRef}
          className="flex snap-x snap-mandatory gap-2 overflow-x-auto px-0.5 pb-3 scroll-smooth hide-scrollbar [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden sm:gap-5 sm:px-1"
          dir="rtl"
        >
          {loopedCards.map((course, index) => (
            <motion.button
              key={course.id}
              type="button"
              onClick={() => navigate(`/courses/${course.pathSegment}`)}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.45, delay: index * 0.04 }}
              whileHover={{ y: -6, scale: 1.005 }}
              className="group relative min-w-[52vw] max-w-[52vw] snap-start overflow-hidden rounded-[14px] border border-slate-200/90 bg-white text-right shadow-[0_12px_20px_-18px_rgba(15,23,42,0.26)] transition hover:border-[#1C3B9B]/25 sm:min-w-[280px] sm:max-w-[280px] sm:rounded-[20px]"
            >
              <div className="relative h-28 overflow-hidden rounded-t-[14px] sm:h-36 sm:rounded-t-[20px]">
                <img
                  src={course.thumbnail}
                  alt={course.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading={index < 3 ? 'eager' : 'lazy'}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
                {course.discount > 0 ? (
                  <span className="absolute right-2 top-2 z-20 rounded-full bg-[#dc2626] px-2.5 py-1 text-[10px] font-bold text-white shadow-[0_10px_18px_-10px_rgba(220,38,38,0.65)]">
                    %{course.discount}
                  </span>
                ) : null}
              </div>

              <div className="grid min-h-[176px] grid-rows-[2.6rem_1.2rem_1.2rem_1.2rem_1fr] gap-1.5 p-2.5 sm:min-h-[182px] sm:grid-rows-[3rem_1.25rem_1.25rem_1fr] sm:gap-2 sm:p-4">
                <h3 className="line-clamp-2 h-[2.6rem] text-[15px] font-extrabold leading-5 text-[#0f172a] group-hover:text-[#1C3B9B] sm:h-12 sm:text-base">
                  {course.title}
                </h3>

                <div className="flex h-[1.2rem] items-center justify-between gap-1 text-[10px] sm:h-5 sm:text-[11px]">
                  <span className="inline-flex min-w-0 items-center gap-1 text-amber-500">
                    <Star className="h-3.5 w-3.5 fill-current" />
                    <span className="truncate font-bold">
                      {course.rating > 0 ? course.rating.toLocaleString('fa-IR') : '—'}
                    </span>
                  </span>
                  <span className="truncate text-slate-400">امتیاز کاربران</span>
                </div>

                <div className="flex h-[1.2rem] items-center justify-between gap-1 text-[10px] text-slate-500 sm:h-5 sm:text-[11px]">
                  <span className="inline-flex min-w-0 items-center gap-1">
                    <UserRound className="h-3.5 w-3.5 text-slate-400" />
                    <span className="truncate">{course.instructor}</span>
                  </span>
                  <span className="inline-flex min-w-0 items-center gap-1">
                    <Clock3 className="h-3.5 w-3.5 text-slate-400" />
                    <span className="truncate">{course.duration}</span>
                  </span>
                </div>

                <div className="flex h-[1.2rem] items-center justify-between gap-1 text-[10px] text-[#64748b] sm:h-5 sm:text-[11px]">
                  <span className="truncate">{course.lessonsCount.toLocaleString('fa-IR')} جلسه</span>
                  <span className="inline-flex min-w-0 items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#C11277]" />
                    <span className="truncate">آنلاین</span>
                  </span>
                </div>

                <div className="mt-auto flex h-auto items-end justify-between gap-1">
                  <div className="flex min-h-[2.2rem] flex-col items-start justify-end text-right sm:min-h-10">
                    {course.oldPrice > 0 && course.price > 0 ? (
                      <p className="text-[11px] font-medium text-slate-400 line-through decoration-1 decoration-red-400/80">
                        {course.oldPrice.toLocaleString('fa-IR')} تومان
                      </p>
                    ) : (
                      <p className="text-[11px] text-transparent">-</p>
                    )}
                    {course.price > 0 ? (
                      <p className="text-[15px] font-black text-[#0f172a] sm:text-sm">
                        {course.price.toLocaleString('fa-IR')} تومان
                      </p>
                    ) : (
                      <FreeBadge size="sm" className="shadow-none" />
                    )}
                  </div>
                  <div className="flex min-w-0 items-center gap-1 text-[11px] font-bold text-[#1C3B9B] sm:gap-1.5 sm:text-xs">
                    <span className="truncate sm:hidden">مشاهده</span>
                    <span className="hidden truncate sm:inline">مشاهده جزئیات</span>
                    <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" />
                  </div>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    ) : (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center text-sm text-[#64748b]">
        دوره‌ای برای نمایش وجود ندارد.
      </div>
    )}
  </section>
);

export default HomeCourseCarouselSection;
